import { useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';

interface IBusinessPiePoint {
  name: string;
  count: number;
}

interface IBusinessPieChartProps {
  data: IBusinessPiePoint[];
  colors: string[];
}

const BusinessPieChart = ({ data, colors }: IBusinessPieChartProps) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<am5.Root | null>(null);

  useLayoutEffect(() => {
    if (!chartRef.current) {
      return;
    }

    rootRef.current?.dispose();

    const root = am5.Root.new(chartRef.current);
    rootRef.current = root;
    root._logo?.dispose();

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout,
      })
    );

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: 'count',
        categoryField: 'name',
      })
    );

    series.get('colors')?.set(
      'colors',
      colors.map((color) => am5.color(color))
    );

    series.slices.template.setAll({
      stroke: am5.color(0xffffff),
      strokeWidth: 1,
      tooltipText: '{category}: {value.formatNumber("#,###")}',
    });

    series.labels.template.set('visible', false);
    series.ticks.template.set('visible', false);
    series.data.setAll(data);

    return () => {
      root.dispose();
      rootRef.current = null;
    };
  }, [data, colors]);

  return (
    <div
      className="amcharts-wrapper"
      data-testid="pie-chart"
      ref={chartRef}
      style={{ width: '100%', height: '180px' }}
    />
  );
};

export default BusinessPieChart;
